import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useShop } from '../contexts/ShopContext';
import { useToast } from '../contexts/ToastContext';
import { useMerchantAnalytics } from '../hooks/useMerchantAnalytics';
import SalesChart from './analytics/SalesChart';
import { ModelRow, RangePicker } from './analytics/ModelRow';
import {
  formatDelta,
  formatMoney,
  GAP_LABEL,
  missedDemand,
  pctChange,
  RANGES,
  STATUS_LABEL,
  type NetworkModel,
  type RangeDays,
} from '../lib/analytics';

/**
 * Analytics: your sales, what is selling across the FlowStock network, and what your store
 * is missing. Network demand is a rank and a band — never another store's figures.
 */

const HOT_SHOWN = 20;
const GROUP_SHOWN = 8;

const Tile: React.FC<{ label: string; value: string; delta?: number | null; hint?: string }> = ({
  label,
  value,
  delta,
  hint,
}) => (
  <div className="glass-card rounded-2xl p-5">
    <p className="text-xs text-white/50 mb-2">{label}</p>
    <p className="font-heading font-bold text-white text-2xl tabular-nums">{value}</p>
    {delta !== undefined && (
      <p className="text-xs text-white/50 mt-1">
        <span className={delta === null ? '' : delta >= 0 ? 'text-emerald-400' : 'text-red-400'}>
          {delta !== null && (delta >= 0 ? '▲ ' : '▼ ')}
          {formatDelta(delta)}
        </span>{' '}
        vs previous period
      </p>
    )}
    {hint && <p className="text-xs text-white/50 mt-1">{hint}</p>}
  </div>
);

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode; aside?: React.ReactNode }> = ({
  title,
  subtitle,
  children,
  aside,
}) => (
  <section className="glass-card rounded-2xl p-6">
    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
      <div>
        <h2 className="font-heading font-bold text-white text-lg">{title}</h2>
        {subtitle && <p className="text-sm text-white/50 mt-0.5">{subtitle}</p>}
      </div>
      {aside}
    </div>
    {children}
  </section>
);

const Skeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div className="space-y-3 py-2" aria-busy="true">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-12 rounded-lg bg-white/[0.04] animate-pulse" />
    ))}
  </div>
);

const Analytics: React.FC = () => {
  const { currentShopId } = useShop();
  const toast = useToast();
  const [days, setDays] = useState<RangeDays>(30);
  const [metric, setMetric] = useState<'revenue' | 'orders'>('revenue');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const a = useMerchantAnalytics(currentShopId, days);

  const gaps = useMemo(() => missedDemand(a.models ?? [], a.prices), [a.models, a.prices]);

  if (!currentShopId) {
    return (
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <p className="text-white/60">Select a shop first.</p>
      </main>
    );
  }

  const o = a.overview;
  const money = (v: number) => formatMoney(v, o?.currency ?? null);

  const add = async (model: NetworkModel) => {
    try {
      const from = await a.addModel(model.sku);
      toast.success(
        from.length
          ? `Added ${model.title || model.sku} from ${from.join(', ')}. It appears in your store after the next sync.`
          : `${model.title || model.sku} is already selected.`,
      );
    } catch (e: any) {
      toast.error(e?.message || 'Could not add this model');
    }
  };

  const restore = async (model: NetworkModel) => {
    try {
      await a.restoreModel(model.sku);
      toast.success(`Restored ${model.title || model.sku}. It returns to your store after the next sync.`);
    } catch (e: any) {
      toast.error(e?.message || 'Could not restore this model');
    }
  };

  const actionFor = (model: NetworkModel) => {
    const busy = a.pending.has(model.sku);
    if (model.status === 'available') {
      return (
        <button
          onClick={() => add(model)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-black text-sm font-semibold px-4 py-1.5 transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          {busy ? 'Adding…' : 'Add to store'}
        </button>
      );
    }
    if (model.status === 'removed') {
      return (
        <button
          onClick={() => restore(model)}
          disabled={busy}
          className="rounded-full border border-white/20 hover:bg-white/[0.06] disabled:opacity-50 text-white text-sm px-4 py-1.5 transition-colors"
        >
          {busy ? 'Restoring…' : 'Restore'}
        </button>
      );
    }
    if (model.status === 'other_supplier') {
      return (
        <Link to="/providers" className="rounded-full border border-white/20 hover:bg-white/[0.06] text-white text-sm px-4 py-1.5 transition-colors">
          Find a supplier
        </Link>
      );
    }
    return null;
  };

  const noteFor = (model: NetworkModel): React.ReactNode => {
    switch (model.status) {
      case 'selling':
        return (
          <span className="inline-flex items-center gap-1 text-emerald-400">
            <CheckIcon className="w-3.5 h-3.5" />
            You sold {model.my_units} this period
          </span>
        );
      case 'available': {
        const from = model.addable_from.join(', ');
        const price = model.from_price ? ` · from ${formatMoney(model.from_price, o?.currency ?? null, 2)} cost` : '';
        return model.addable_in_stock
          ? `Not in your store · in stock at ${from}${price}`
          : `Not in your store · ${from} carries it, currently out of stock`;
      }
      case 'other_supplier':
        return `Not in your store · ${model.other_suppliers} supplier${model.other_suppliers === 1 ? '' : 's'} you are not connected to ${model.other_suppliers === 1 ? 'has' : 'have'} it`;
      default:
        return STATUS_LABEL[model.status];
    }
  };

  const group = (key: string, title: string, hint: string, items: NetworkModel[], note?: (m: NetworkModel) => React.ReactNode) => {
    if (!items.length) return null;
    const shown = expanded[key] ? items : items.slice(0, GROUP_SHOWN);
    return (
      <div className="mt-5 first:mt-0">
        <h3 className="text-sm font-semibold text-white">
          {title} <span className="text-white/40 font-normal">({items.length})</span>
        </h3>
        <p className="text-xs text-white/45 mb-1">{hint}</p>
        <ul>
          {shown.map((m) => (
            <ModelRow
              key={m.sku}
              rank={m.rank}
              sku={m.sku}
              title={m.title}
              brand={m.brand}
              photoUrl={m.photo_url}
              demand={m.demand}
              trend={m.trend}
              note={(note ?? noteFor)(m)}
              action={actionFor(m)}
            />
          ))}
        </ul>
        {items.length > GROUP_SHOWN && (
          <button
            onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
            className="text-xs text-orange-400 hover:text-orange-300 mt-1"
          >
            {expanded[key] ? 'Show fewer' : `Show all ${items.length}`}
          </button>
        )}
      </div>
    );
  };

  const priceBySku = new Map(gaps.lostOnPrice.map((g) => [g.model.sku, g.price]));
  const gapCount =
    gaps.addable.length + gaps.needSupplier.length + gaps.lostOnPrice.length + gaps.soldOut.length + gaps.unsupplied.length;
  const supplierShare =
    o && o.totals.units > 0 ? Math.round((o.totals.supplier_units / o.totals.units) * 100) : null;

  return (
    <div className="min-h-screen">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white mb-2">
              <ArrowLeftIcon className="w-4 h-4" />
              Dashboard
            </Link>
            <h1 className="font-heading font-bold text-white text-3xl">Analytics</h1>
            <p className="text-white/50 text-sm mt-1">
              Your sales, what is selling across the FlowStock network, and what your store is missing.
            </p>
          </div>
          <RangePicker value={days} options={RANGES} onChange={setDays} />
        </div>

        {a.error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-4 py-3">{a.error}</div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Tile label="Sales" value={o ? money(o.totals.revenue) : '…'} delta={o ? pctChange(o.totals.revenue, o.previous_totals.revenue) : undefined} />
          <Tile label="Orders" value={o ? String(o.totals.orders) : '…'} delta={o ? pctChange(o.totals.orders, o.previous_totals.orders) : undefined} />
          <Tile
            label="Average order"
            value={o ? (o.totals.orders ? money(o.totals.revenue / o.totals.orders) : '—') : '…'}
            delta={
              o && o.totals.orders && o.previous_totals.orders
                ? pctChange(o.totals.revenue / o.totals.orders, o.previous_totals.revenue / o.previous_totals.orders)
                : o ? null : undefined
            }
          />
          <Tile
            label="Sold from supplier stock"
            value={o ? (supplierShare === null ? '—' : `${supplierShare}%`) : '…'}
            hint={o ? `${o.totals.supplier_units} of ${o.totals.units} units` : undefined}
          />
        </div>

        <Section
          title={metric === 'revenue' ? 'Sales over time' : 'Orders over time'}
          subtitle={o ? `Last ${o.days} days against the ${o.days} days before · order totals, cancelled orders excluded` : undefined}
          aside={
            <div className="inline-flex rounded-lg border border-white/10 overflow-hidden" role="group" aria-label="Measure">
              {(['revenue', 'orders'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setMetric(m)}
                  aria-pressed={metric === m}
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    metric === m ? 'bg-white/[0.12] text-white' : 'text-white/60 hover:bg-white/[0.06]'
                  }`}
                >
                  {m === 'revenue' ? 'Sales' : 'Orders'}
                </button>
              ))}
            </div>
          }
        >
          {o ? (
            <>
              <SalesChart
                series={o.series}
                previous={o.previous_series}
                metric={metric}
                formatValue={metric === 'revenue' ? money : (v) => String(Math.round(v))}
              />
              {o.totals.other_currency_orders > 0 && (
                <p className="text-xs text-white/40 mt-2">
                  {o.totals.other_currency_orders} order{o.totals.other_currency_orders === 1 ? '' : 's'} in another
                  currency {o.totals.other_currency_orders === 1 ? 'is' : 'are'} not included.
                </p>
              )}
            </>
          ) : (
            <div className="h-64 rounded-lg bg-white/[0.04] animate-pulse" aria-busy="true" />
          )}
        </Section>

        <Section
          title="Hot right now"
          subtitle={`The top-selling models across the FlowStock network in the last ${days} days.`}
        >
          {a.models === null ? (
            <Skeleton />
          ) : a.models.length === 0 ? (
            <p className="text-sm text-white/50 py-4">Not enough sales in this period to rank models yet.</p>
          ) : (
            <ul>
              {a.models.slice(0, HOT_SHOWN).map((m) => {
                const price = priceBySku.get(m.sku);
                return (
                  <ModelRow
                    key={m.sku}
                    rank={m.rank}
                    sku={m.sku}
                    title={m.title}
                    brand={m.brand}
                    photoUrl={m.photo_url}
                    demand={m.demand}
                    trend={m.trend}
                    note={
                      price?.gap_band ? (
                        <>
                          {noteFor(m)} · <span className="text-yellow-400">priced {GAP_LABEL[price.gap_band]} where it sells</span>
                        </>
                      ) : (
                        noteFor(m)
                      )
                    }
                    action={actionFor(m)}
                  />
                );
              })}
            </ul>
          )}
        </Section>

        <Section
          title="Missed demand"
          subtitle="Hot models your store is not getting the sales for, hottest first."
        >
          {a.models === null ? (
            <Skeleton rows={4} />
          ) : gapCount === 0 ? (
            <p className="text-sm text-white/50 py-4">
              {a.pricesLoading ? 'Checking your prices…' : 'No gaps: you list every hot model, in stock and at a competitive price.'}
            </p>
          ) : (
            <>
              {group('addable', 'Not in your store', 'Carried by suppliers you are already connected to.', gaps.addable)}
              {group(
                'price',
                'Losing on price',
                'In your store, but priced above where this model is selling in the network.',
                gaps.lostOnPrice.map((g) => g.model),
                (m) => {
                  const p = priceBySku.get(m.sku)!;
                  const yours =
                    p.my_price !== null
                      ? `Your price ${formatMoney(p.my_price, p.currency, 2)}${
                          p.my_price_max && p.my_price_max > p.my_price ? `–${formatMoney(p.my_price_max, p.currency, 2)}` : ''
                        }`
                      : 'Your price';
                  return (
                    <>
                      {yours} · <span className="text-yellow-400">{p.gap_band ? GAP_LABEL[p.gap_band] : 'above'} where it sells</span>
                      {m.my_units > 0 ? ` · you sold ${m.my_units}` : ' · no sales this period'}
                    </>
                  );
                },
              )}
              {group('soldout', 'Out of stock in your store', 'Listed, selling elsewhere, and nothing to sell here right now.', gaps.soldOut)}
              {group('connect', 'Needs another supplier', 'Only suppliers you are not connected to have these.', gaps.needSupplier)}
              {group('unsupplied', 'No supplier yet', 'Selling from other stores’ own stock. We show these to suppliers as demand to fill.', gaps.unsupplied)}
              {a.pricesLoading && <p className="text-xs text-white/40 mt-4">Checking your prices…</p>}
            </>
          )}
        </Section>

        <p className="text-xs text-white/35">
          Network demand is shown as a rank and a demand level. No other store’s sales, prices or names are shown, and
          yours are not shown to anyone else. Price comparisons cover models you have sold before.
        </p>
      </main>
    </div>
  );
};

export default Analytics;
